import styled from '@emotion/styled'
import { Box, capitalize, Typography } from '@mui/material'
import React from 'react'

import { CTAButton } from '@f-fertighaus/applications/Next/components/common/CTAButton'
import { MobileOnly } from '@f-fertighaus/applications/Next/components/common/MediaQuery'
import { HdpNavigationTab } from '@f-fertighaus/applications/Next/components/common/navigation/tabs/useActiveTabs'
import { PercentSignIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/PercentSignIcon'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'
import { PricesList } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/PriceSection/PricesList'
import { SalesOfficeForVendor } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/components/SalesOfficeForVendor'
import { useUserPostcode } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/hooks/useUserPostcode'
import { ClientOnly } from '@f-fertighaus/components/ClientOnly/ClientOnly'

export const PriceSection = () => {
  const {
    state: { name, active },
    vendor,
    domain
  } = useHouseDetail()

  const postcode = useUserPostcode()

  const handleOfferComparisonCtaClick = () => {
    webtrack({
      category: 'CTA-button',
      action: 'click',
      label: 'offer-comparison',
      indicates: ['funnel-entry'],
      location: 'price-section',
      values: [
        {
          type: 'text',
          value: t('houseDetailPage.sections.price.compareOffers.buttonText')
        }
      ]
    })
  }

  const handlePersonalOfferCtaClick = () => {
    webtrack({
      category: 'CTA-button',
      action: 'click',
      label: 'personal-offer',
      indicates: ['funnel-entry'],
      location: 'price-section',
      values: [
        { type: 'text', value: t('houseDetailPage.sections.price.mainCtaText') }
      ]
    })
  }

  const isDisabled = !active

  return (
    <Wrapper>
      <HeadlineBox>
        <Typography
          component='h2'
          id={`navigation-category-${HdpNavigationTab.price}`}>
          <Typography variant='mediumTitle' component='span' display='block'>
            {t('houseDetailPage.sections.price.headline')}
          </Typography>
          <Typography component='span' variant='body2' display='block'>
            {t('houseDetailPage.heading_suffix', { houseName: name })}
          </Typography>
        </Typography>
      </HeadlineBox>
      <Content>
        <PricesList />
        <DisclaimerParagraph variant='body1'>
          {t('houseDetailPage.sections.price.disclaimer')}
        </DisclaimerParagraph>
        {active && (
          <MobileOnly>
            <Box sx={{ marginBottom: 4 }}>
              <ClientOnly>
                {postcode ? (
                  <SalesOfficeForVendor
                    postcode={postcode}
                    vendorNid={vendor.nid}>
                    <Typography fontWeight='fontWeightBold' variant='body2'>
                      {vendor.display_name}
                    </Typography>
                  </SalesOfficeForVendor>
                ) : (
                  <SalesOfficeForVendor.DefaultContact>
                    <Typography fontWeight='fontWeightBold' variant='body2'>
                      {vendor.display_name}
                    </Typography>
                  </SalesOfficeForVendor.DefaultContact>
                )}
              </ClientOnly>
              <CTAButton
                variant='contained'
                color='primary'
                className='js-request-button'
                data-request-type='house-detail-form'
                data-funnel-variant='house-wizard-item-offer'
                sx={{ marginTop: 2 }}
                onClick={handlePersonalOfferCtaClick}>
                {t('houseDetailPage.sections.price.mainCtaText')}
              </CTAButton>
            </Box>
          </MobileOnly>
        )}
        <OffersComparisonHeader>
          <PercentSignIcon />
          <Typography
            variant='body1'
            fontWeight='fontWeightBold'
            sx={{ marginLeft: 3 }}>
            {t('houseDetailPage.sections.price.compareOffers.headline')}
          </Typography>
        </OffersComparisonHeader>
        <Typography variant='body1'>
          {t('houseDetailPage.sections.price.compareOffers.contentText', {
            domain: capitalize(domain)
          })}
        </Typography>
        <CTAButton
          disabled={isDisabled}
          variant='contained'
          color='primary'
          className='js-request-button'
          data-request-type='house-detail-form'
          data-funnel-variant='house-wizard-item-comparison'
          sx={{ marginTop: 2 }}
          onClick={handleOfferComparisonCtaClick}>
          {t('houseDetailPage.sections.price.compareOffers.buttonText')}
        </CTAButton>
      </Content>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${({ theme }) => theme.spacing(8)};
  }
`

export const Content = styled.div`
  padding: ${({ theme }) => theme.spacing(3, 0, 0, 0)};
`

const HeadlineBox = styled.div`
  padding: ${({ theme }) => theme.spacing(2, 0, 0, 0)};
`

const DisclaimerParagraph = styled(Typography)`
  margin: ${({ theme }) => theme.spacing(3)} 0;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${({ theme }) => theme.spacing(4)};
  }
`

const OffersComparisonHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  padding: 0 ${({ theme }) => theme.spacing(1)};
`
